import RankScheduleSettings from '../components/RankScheduleSettings';
import WeeklySummarySettings from '../components/WeeklySummarySettings';
import AlertRulesSettings from '../components/AlertRulesSettings';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const SCHEDULE_TYPES = { daily: 'יומי', weekly: 'שבועי', monthly: 'חודשי' };

export default function SystemAdmin() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [health, setHealth] = useState(null);
  const [costs, setCosts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningTask, setRunningTask] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [activeTab, setActiveTab] = useState('tasks');
  const [signatures, setSignatures] = useState([]);
  const [sigForm, setSigForm] = useState({ id: null, name: '', content: '', is_default: false });

  const loadAll = useCallback(async () => {
    try {
      const [t, h, c] = await Promise.all([
        api.getScheduledTasks(),
        api.getSystemHealth(),
        api.getCostsSummary(),
      ]);
      setTasks(t);
      setHealth(h);
      setCosts(c);
    } catch (err) {
      console.error('Failed to load system data:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadSignatures = useCallback(async () => {
    try { setSignatures(await api.getSignatures()); } catch (e) {}
  }, []);
  useEffect(() => { loadSignatures(); }, [loadSignatures]);

  async function saveSignature() {
    if (!sigForm.name.trim() || !sigForm.content.trim()) { alert('נא למלא שם ותוכן חתימה'); return; }
    try {
      const payload = { name: sigForm.name, content: sigForm.content, is_default: sigForm.is_default };
      if (sigForm.id) await api.updateSignature(sigForm.id, payload);
      else await api.createSignature(payload);
      setSigForm({ id: null, name: '', content: '', is_default: false });
      await loadSignatures();
    } catch (err) { alert('שגיאה: ' + err.message); }
  }
  function editSignature(s) { setSigForm({ id: s.id, name: s.name, content: s.content, is_default: !!s.is_default }); }
  async function deleteSignature(s) {
    if (!window.confirm('למחוק את החתימה "' + s.name + '"?')) return;
    try { await api.deleteSignature(s.id); if (sigForm.id === s.id) setSigForm({ id: null, name: '', content: '', is_default: false }); await loadSignatures(); }
    catch (e) { alert('שגיאה: ' + e.message); }
  }
  async function makeDefaultSignature(s) {
    try { await api.setDefaultSignature(s.id); await loadSignatures(); } catch (e) { alert('שגיאה: ' + e.message); }
  }
  async function handleSignatureImage(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const res = await api.uploadSignatureImage(reader.result);
        // Prepend the image (absolute URL, so it renders in the client's email).
        const img = '<img src="' + (res.absolute_url || res.url) + '" alt="" style="max-width:200px;height:auto;display:block;margin-bottom:8px;" />';
        setSigForm(v => ({ ...v, content: img + (v.content || '') }));
      } catch (err) { alert('שגיאה בהעלאת תמונה: ' + err.message); }
    };
    reader.readAsDataURL(file);
  }

  useEffect(() => {
    const interval = setInterval(async () => {
      try { setHealth(await api.getSystemHealth()); } catch (e) {}
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  async function handleToggle(task) {
    try {
      await api.updateScheduledTask(task.id, { enabled: !task.enabled });
      setTasks(prev => prev.map(t => t.id === task.id ? { ...t, enabled: !t.enabled } : t));
    } catch (err) { alert('שגיאה: ' + err.message); }
  }

  async function handleRunNow(task) {
    if (!window.confirm(`להריץ "${task.name}" עכשיו?`)) return;
    setRunningTask(task.id);
    try {
      await api.runScheduledTask(task.id);
      await loadAll();
    } catch (err) { alert('שגיאה: ' + err.message); }
    setRunningTask(null);
  }

  function startEdit(task) {
    setEditingTask(task.id);
    setEditValues({ hour: task.hour, minute: task.minute, day_of_month: task.day_of_month, day_of_week: task.day_of_week });
  }

  async function saveEdit(task) {
    try {
      const params = { hour: editValues.hour, minute: editValues.minute };
      if (task.schedule_type === 'monthly') params.day_of_month = editValues.day_of_month;
      if (task.schedule_type === 'weekly') params.day_of_week = editValues.day_of_week;
      await api.updateScheduledTask(task.id, params);
      setEditingTask(null);
      await loadAll();
    } catch (err) { alert('שגיאה: ' + err.message); }
  }

  function timeAgo(isoStr) {
    if (!isoStr) return 'לא רץ';
    const diff = Date.now() - new Date(isoStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'עכשיו';
    if (mins < 60) return `לפני ${mins} דק׳`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `לפני ${hours} שע׳`;
    const days = Math.floor(hours / 24);
    return `לפני ${days} ימים`;
  }

  if (loading) return <div className="loading">טוען...</div>;

  const tabs = [
    { key: 'tasks', label: 'משימות מתוזמנות', icon: '⏰' },
    { key: 'health', label: 'בריאות מערכת', icon: '💚' },
    { key: 'costs', label: 'עלויות API', icon: '💰' },
    { key: 'rank_schedule', label: 'בדיקת מיקומים', icon: '📊' },
    { key: 'signatures', label: 'חתימות מייל', icon: '✍️' },
  ];

  return (
    <>
      <div className="page-header" style={{ marginBottom: '6px' }}>
        <div>
          <div className="page-title">ניהול מערכת</div>
          <div className="page-subtitle">משימות מתוזמנות, בריאות מערכת, עלויות</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '9px 18px', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: '13px', fontFamily: 'var(--sans)', fontWeight: 500,
              transition: 'all 0.15s',
              background: activeTab === tab.key ? 'var(--accent)' : 'var(--surface)',
              color: activeTab === tab.key ? '#fff' : 'var(--text-secondary)',
              border: activeTab === tab.key ? '1px solid var(--accent)' : '1px solid var(--border)',
            }}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* === TASKS TAB === */}
      {activeTab === 'tasks' && (
        <div className="animate-in" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {tasks.length === 0 ? (
            <div className="empty-state"><p>אין משימות מוגדרות</p></div>
          ) : tasks.map(task => (
            <div key={task.id} style={{
              background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
              padding: '16px 18px', opacity: task.enabled ? 1 : 0.45, transition: 'opacity 0.2s',
            }}>
              {/* Row 1: Toggle + Name + Actions */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div onClick={() => handleToggle(task)} style={{
                    width: '38px', height: '21px', borderRadius: '11px', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                    background: task.enabled ? 'var(--green)' : 'var(--border-light)',
                  }}>
                    <div style={{
                      width: '17px', height: '17px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '2px', transition: 'left 0.2s',
                      left: task.enabled ? '2px' : '19px',
                    }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '14px', lineHeight: 1.3 }}>{task.name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '1px' }}>{task.description}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                  <button onClick={() => editingTask === task.id ? setEditingTask(null) : startEdit(task)}
                    style={{
                      padding: '5px 10px', background: editingTask === task.id ? 'var(--accent-soft)' : 'var(--surface-hover)', border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '12px', fontFamily: 'var(--sans)',
                    }}>✏️</button>
                  <button onClick={() => handleRunNow(task)} disabled={runningTask === task.id}
                    style={{
                      padding: '5px 12px', background: 'var(--accent-soft)', border: '1px solid var(--accent)', borderRadius: 'var(--radius-sm)',
                      color: 'var(--accent)', cursor: runningTask === task.id ? 'wait' : 'pointer', fontSize: '12px', fontFamily: 'var(--sans)',
                    }}>
                    {runningTask === task.id ? '⏳ רץ...' : '▶ הרץ'}
                  </button>
                </div>
              </div>

              {/* Row 2: Schedule info */}
              <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'var(--mono)', flexWrap: 'wrap' }}>
                <span style={{ background: 'var(--bg)', padding: '3px 10px', borderRadius: '4px', border: '1px solid var(--border)' }}>
                  {SCHEDULE_TYPES[task.schedule_type]} | {task.schedule_display}
                </span>
                <span style={{ padding: '3px 0' }}>
                  הרצה אחרונה: {timeAgo(task.last_run)}
                </span>
                {task.last_status && (
                  <span style={{ color: task.last_error ? 'var(--red)' : 'var(--green)', padding: '3px 0' }}>
                    {task.last_error ? '✗ ' + task.last_error.substring(0, 50) : '✓ ' + task.last_status}
                  </span>
                )}
              </div>

              {/* Edit panel */}
              {editingTask === task.id && (
                <div style={{
                  marginTop: '12px', padding: '12px 14px', background: 'var(--bg)', borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
                }}>
                  <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    שעה:
                    <input type="number" min="0" max="23" value={editValues.hour}
                      onChange={e => setEditValues(v => ({ ...v, hour: parseInt(e.target.value) || 0 }))}
                      style={{ width: '48px', padding: '5px 6px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '4px', color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: '13px', textAlign: 'center' }} />
                  </label>
                  <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    דקה:
                    <input type="number" min="0" max="59" value={editValues.minute}
                      onChange={e => setEditValues(v => ({ ...v, minute: parseInt(e.target.value) || 0 }))}
                      style={{ width: '48px', padding: '5px 6px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '4px', color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: '13px', textAlign: 'center' }} />
                  </label>
                  {task.schedule_type === 'monthly' && (
                    <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      יום בחודש:
                      <input type="number" min="1" max="28" value={editValues.day_of_month || 28}
                        onChange={e => setEditValues(v => ({ ...v, day_of_month: parseInt(e.target.value) || 28 }))}
                        style={{ width: '48px', padding: '5px 6px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '4px', color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: '13px', textAlign: 'center' }} />
                    </label>
                  )}
                  <div style={{ display: 'flex', gap: '6px', marginRight: 'auto' }}>
                    <button onClick={() => saveEdit(task)}
                      style={{ padding: '5px 14px', background: 'var(--green)', border: 'none', borderRadius: '4px', color: '#fff', cursor: 'pointer', fontSize: '12px', fontFamily: 'var(--sans)', fontWeight: 500 }}>
                      שמור
                    </button>
                    <button onClick={() => setEditingTask(null)}
                      style={{ padding: '5px 14px', background: 'transparent', border: '1px solid var(--border)', borderRadius: '4px', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '12px', fontFamily: 'var(--sans)' }}>
                      ביטול
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* === HEALTH TAB === */}
      {activeTab === 'health' && health && (
        <div className="animate-in">
          <div style={{
            padding: '14px 18px', borderRadius: 'var(--radius)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px',
            background: health.overall === 'ok' ? 'var(--green-soft)' : health.overall === 'warning' ? 'var(--yellow-glow)' : 'var(--red-glow)',
            border: `1px solid ${health.overall === 'ok' ? 'var(--green)' : health.overall === 'warning' ? 'var(--yellow)' : 'var(--red)'}`,
          }}>
            <span style={{ fontSize: '22px' }}>{health.overall === 'ok' ? '✅' : health.overall === 'warning' ? '⚠️' : '❌'}</span>
            <div>
              <div style={{ fontWeight: 600, fontSize: '14px' }}>
                {health.overall === 'ok' ? 'כל המערכות תקינות' : health.overall === 'warning' ? 'יש אזהרות' : 'יש בעיות'}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', fontFamily: 'var(--mono)' }}>
                v{health.version} | {new Date(health.server_time).toLocaleString('he-IL')}
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '10px' }}>
            {Object.entries(health.checks).map(([name, check]) => {
              const statusColor = check.status === 'ok' ? 'var(--green)' : check.status === 'warning' ? 'var(--yellow)' : 'var(--red)';
              const statusBg = check.status === 'ok' ? 'var(--green-glow)' : check.status === 'warning' ? 'var(--yellow-glow)' : 'var(--red-glow)';
              const labels = { postgresql: 'PostgreSQL', redis: 'Redis', celery: 'Celery Workers', dataforseo: 'DataForSEO', google_oauth: 'Google OAuth', wordpress: 'WordPress API' };
              return (
                <div key={name} style={{
                  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 16px',
                  borderRight: `3px solid ${statusColor}`,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ fontWeight: 600, fontSize: '13px' }}>{labels[name] || name}</div>
                    <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, background: statusBg, color: statusColor }}>
                      {check.status === 'ok' ? 'תקין' : check.status === 'warning' ? 'אזהרה' : 'שגיאה'}
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', fontFamily: 'var(--mono)', lineHeight: 1.6 }}>
                    {check.error && <div style={{ color: 'var(--red)' }}>{check.error}</div>}
                    {check.sites !== undefined && <div>אתרים: {check.sites} | ביטויים: {check.keywords}</div>}
                    {check.memory_used && <div>זיכרון: {check.memory_used}</div>}
                    {check.workers !== undefined && <div>Workers: {check.workers} | פעילים: {check.active_tasks}</div>}
                    {check.configured !== undefined && <div>מוגדר ✓</div>}
                    {check.connected_sites !== undefined && <div>אתרים מחוברים: {check.connected_sites}</div>}
                    {check.configured_sites !== undefined && <div>אתרים עם API: {check.configured_sites}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* === COSTS TAB === */}
      {activeTab === 'costs' && costs && (
        <div className="animate-in">
          <div className="stats-row">
            <div className="stat-card">
              <div className="stat-value" style={{ color: 'var(--accent)' }}>${costs.today.toFixed(3)}</div>
              <div className="stat-label">היום</div>
            </div>
            <div className="stat-card">
              <div className="stat-value" style={{ color: 'var(--orange)' }}>${costs.this_month.toFixed(3)}</div>
              <div className="stat-label">החודש</div>
            </div>
          </div>

          {Object.keys(costs.by_service).length > 0 && (
            <div className="detail-section" style={{ marginTop: '16px' }}>
              <div className="detail-section-title">לפי שירות - החודש</div>
              {Object.entries(costs.by_service).map(([service, cost]) => (
                <div className="detail-row" key={service}>
                  <span className="detail-label">{service}</span>
                  <span className="detail-value" style={{ fontFamily: 'var(--mono)' }}>${cost.toFixed(4)}</span>
                </div>
              ))}
            </div>
          )}

          {costs.daily_trend && costs.daily_trend.length > 0 && (
            <div className="detail-section" style={{ marginTop: '16px' }}>
              <div className="detail-section-title">מגמה - 7 ימים אחרונים</div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', padding: '16px 16px 8px', height: '130px' }}>
                {costs.daily_trend.map((d, i) => {
                  const maxCost = Math.max(...costs.daily_trend.map(x => x.cost), 0.001);
                  const height = Math.max((d.cost / maxCost) * 80, 3);
                  return (
                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                      <div style={{ fontSize: '10px', fontFamily: 'var(--mono)', color: 'var(--text-muted)', minHeight: '14px' }}>
                        {d.cost > 0 ? `$${d.cost.toFixed(2)}` : ''}
                      </div>
                      <div style={{
                        width: '100%', maxWidth: '40px', height: `${height}px`,
                        background: d.cost > 0 ? 'var(--accent)' : 'var(--border)', borderRadius: '3px 3px 0 0', transition: 'height 0.3s',
                      }} />
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                        {new Date(d.date).toLocaleDateString('he-IL', { day: 'numeric', month: 'numeric' })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ marginTop: '14px', textAlign: 'center' }}>
            <button onClick={() => navigate('/costs')} style={{
              padding: '8px 20px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
              color: 'var(--accent)', cursor: 'pointer', fontSize: '13px', fontFamily: 'var(--sans)',
            }}>
              צפייה בפירוט מלא &larr;
            </button>
          </div>
        </div>
      )}

      {/* === RANK SCHEDULE TAB === */}
      {activeTab === "rank_schedule" && (
        <div className="animate-in">
          <div style={{
            background: "var(--surface)", border: "1px solid var(--border)",
            borderRadius: "var(--radius)", padding: "18px 20px",
          }}>
            <RankScheduleSettings />
          </div>
          <div style={{
            background: "var(--surface)", border: "1px solid var(--border)",
            borderRadius: "var(--radius)", padding: "18px 20px", marginTop: "16px",
          }}>
            <WeeklySummarySettings />
          </div>
          <div style={{
            background: "var(--surface)", border: "1px solid var(--border)",
            borderRadius: "var(--radius)", padding: "18px 20px", marginTop: "16px",
          }}>
            <AlertRulesSettings />
          </div>
        </div>
      )}

      {/* === EMAIL SIGNATURES TAB === */}
      {activeTab === 'signatures' && (
        <div className="animate-in" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Existing signatures */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {signatures.length === 0 ? (
              <div className="empty-state"><p>אין חתימות עדיין. הוסיפו חתימה חדשה למטה.</p></div>
            ) : signatures.map(s => (
              <div key={s.id} style={{
                background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 16px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ fontWeight: 600, fontSize: '14px' }}>{s.name}</div>
                    {s.is_default && (
                      <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, background: 'var(--green-glow)', color: 'var(--green)' }}>ברירת מחדל</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {!s.is_default && (
                      <button onClick={() => makeDefaultSignature(s)}
                        style={{ padding: '5px 10px', background: 'var(--surface-hover)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '12px', fontFamily: 'var(--sans)' }}>קבע כברירת מחדל</button>
                    )}
                    <button onClick={() => editSignature(s)}
                      style={{ padding: '5px 10px', background: 'var(--accent-soft)', border: '1px solid var(--accent)', borderRadius: 'var(--radius-sm)', color: 'var(--accent)', cursor: 'pointer', fontSize: '12px', fontFamily: 'var(--sans)' }}>עריכה</button>
                    <button onClick={() => deleteSignature(s)}
                      style={{ padding: '5px 10px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--red)', cursor: 'pointer', fontSize: '12px', fontFamily: 'var(--sans)' }}>מחיקה</button>
                  </div>
                </div>
                <div style={{ marginTop: '10px', padding: '10px 12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: '13px', color: 'var(--text-secondary)', direction: 'rtl' }}
                  dangerouslySetInnerHTML={{ __html: s.content }} />
              </div>
            ))}
          </div>

          {/* Add / edit form */}
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '16px 18px' }}>
            <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '12px' }}>{sigForm.id ? 'עריכת חתימה' : 'חתימה חדשה'}</div>
            <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>שם החתימה (לזיהוי פנימי)</label>
            <input type="text" value={sigForm.name}
              onChange={e => setSigForm(v => ({ ...v, name: e.target.value }))}
              placeholder="למשל: שחר - חתימה ראשית"
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: '14px', marginBottom: '12px', fontFamily: 'var(--sans)' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap', gap: '8px' }}>
              <label style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>תוכן החתימה (תומך HTML — לשבירת שורה השתמשו ב-&lt;br&gt;)</label>
              <label style={{ padding: '5px 12px', background: 'var(--surface-hover)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '12px' }}>
                🖼️ הוסף תמונה
                <input type="file" accept="image/*" onChange={handleSignatureImage} style={{ display: 'none' }} />
              </label>
            </div>
            <textarea value={sigForm.content}
              onChange={e => setSigForm(v => ({ ...v, content: e.target.value }))}
              rows={5}
              placeholder={'בברכה,<br>צוות אינטרמקס<br>03-1234567'}
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: '13px', direction: 'rtl', fontFamily: 'var(--mono)', resize: 'vertical' }} />
            {sigForm.content.trim() && (
              <div style={{ marginTop: '8px' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>תצוגה מקדימה:</div>
                <div style={{ padding: '10px 12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: '13px', color: 'var(--text-secondary)', direction: 'rtl' }}
                  dangerouslySetInnerHTML={{ __html: sigForm.content }} />
              </div>
            )}
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginTop: '12px', fontSize: '13px', color: 'var(--text-secondary)' }}>
              <input type="checkbox" checked={sigForm.is_default} onChange={e => setSigForm(v => ({ ...v, is_default: e.target.checked }))} />
              קבע כחתימת ברירת מחדל
            </label>
            <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
              <button onClick={saveSignature}
                style={{ padding: '8px 18px', background: 'var(--accent)', border: 'none', borderRadius: 'var(--radius-sm)', color: '#fff', cursor: 'pointer', fontSize: '13px', fontFamily: 'var(--sans)', fontWeight: 500 }}>
                {sigForm.id ? 'שמור שינויים' : 'הוסף חתימה'}
              </button>
              {sigForm.id && (
                <button onClick={() => setSigForm({ id: null, name: '', content: '', is_default: false })}
                  style={{ padding: '8px 18px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '13px', fontFamily: 'var(--sans)' }}>
                  ביטול עריכה
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
